import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useProducts } from "../hooks/useShop";

const filters = ["Alle", "Limited", "Unisex"];

const Shop = () => {
  useScrollReveal();
  const { products, loading } = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("filter") || "Alle";
  const [active, setActive] = useState(initial);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setActive(searchParams.get("filter") || "Alle");
  }, [searchParams]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (active !== "Alle") {
      list = list.filter(
        (p) =>
          p.category === active ||
          p.badge === active ||
          (active === "Limited" && p.badge === "Limited")
      );
    }
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [active, sort, products]);

  const handleFilter = (f) => {
    setActive(f);
    if (f === "Alle") setSearchParams({});
    else setSearchParams({ filter: f });
  };

  return (
    <div className="pt-8">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 text-center py-14 border-b border-[#e7e0d3]">
        <p className="text-gold text-[11px] tracking-[0.4em] uppercase mb-3">
          Die Kollektion
        </p>
        <h1 className="font-serif text-5xl md:text-6xl text-[#17140f]">
          Alle Düfte
        </h1>
        <p className="text-[#6b665a] mt-4 max-w-lg mx-auto">
          Extraits de Parfum, handgefertigt in Grasse — für jeden Charakter der
          passende Duft.
        </p>
      </div>

      {/* Controls */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => handleFilter(f)}
              className={`text-[11px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors ${
                active === f
                  ? "bg-gold text-[#14110c] border-gold"
                  : "border-[#dcd4c5] text-[#4a463c] hover:border-gold hover:text-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[#8d8779] text-xs tracking-[0.15em] uppercase">
            Sortieren
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#f7f1e8] border border-[#dcd4c5] text-[#4a463c] text-sm px-4 py-2.5 focus:outline-none focus:border-gold cursor-pointer"
          >
            <option value="featured">Empfohlen</option>
            <option value="price-asc">Preis: aufsteigend</option>
            <option value="price-desc">Preis: absteigend</option>
            <option value="rating">Beste Bewertung</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pb-24">
        <p className="text-[#8d8779] text-xs mb-8">
          {loading ? "Lädt live aus Shopify…" : `${filtered.length} Produkte`}
        </p>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-[#ece4d6] animate-pulse rounded-sm"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-[#6b665a] py-24">
            Keine Produkte in dieser Kategorie.
          </p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 reveal">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
